export default {
  title: 'Payment gateway',
  description:
    'Your own payment gateways for online orders. The whole order amount settles to your gateway account; you settle with your restaurants.',
  toasts: {
    load_error: 'Failed to load the gateway setup',
  },
  not_enabled: {
    title: 'Spreedly is not enabled for your account yet',
    description:
      'Local payment gateways are activated by FoodAmigos. Please contact your FoodAmigos representative to get started.',
  },
  processor_mode: {
    title: 'Who processes the payments?',
    description:
      'This is set once for all your restaurants. You can switch later in Partner settings as long as no restaurant is live.',
    save: 'Save',
    options: {
      platform: {
        title: 'FoodAmigos processes payments',
        badge: 'Recommended',
        description:
          'We handle the payment account, verification and payouts. Each restaurant adds its business details and completes a short check with Adyen.',
        methods: 'Accepted: {{methods}}',
      },
      provider: {
        title: 'I bring my own provider',
        description: 'Connect a payment provider you already use. Payouts, fees and verification stay with them.',
      },
    },
    payment_methods: {
      card: 'Card',
      apple_pay: 'Apple Pay',
      google_pay: 'Google Pay',
      pay_by_bank: 'Pay by bank',
      giropay: 'giropay',
      sepa_debit: 'SEPA',
    },
    locked: {
      platform: 'FoodAmigos processes payments for all of your restaurants.',
      provider: 'Your restaurants take payments through your own providers.',
    },
    confirm_choice: {
      title: 'Set payments to “{{mode}}”?',
      description:
        'This applies to every restaurant you run. You can switch later from Partner settings, but only while no restaurant is live.',
      confirm: 'Save and continue',
    },
    switch: {
      link: 'Change how payments are processed',
      confirm: {
        confirm: 'Switch',
        provider: {
          title: 'Switch to your own provider?',
          description: 'Restaurants using FoodAmigos payments will go back to Not set up.',
        },
        platform: {
          title: 'Switch to FoodAmigos payments?',
          description: 'Restaurants using your own providers will go back to Not set up.',
        },
      },
      unavailable: {
        title: 'FoodAmigos payments are not available yet',
        description: 'FoodAmigos payments are not available in your country yet.',
        confirm: 'OK',
      },
      blocked: {
        title: 'Contact FoodAmigos to change this',
        description_one:
          '{{count}} restaurant is taking live payments. Switching would mean it re-onboards with the new provider. Our team will plan it with you.',
        description_other:
          '{{count}} restaurants are taking live payments. Switching would mean they re-onboard with the new provider. Our team will plan it with you.',
        confirm: 'Email FoodAmigos',
        subject_one: 'Change how payments are processed ({{count}} live restaurant)',
        subject_other: 'Change how payments are processed ({{count}} live restaurants)',
      },
    },
    toasts: {
      load_error: 'Failed to load the payment setup',
      save_error: 'Failed to save the payment setup',
      chosen: 'Payment processing saved',
      switched: 'Payment processing switched',
    },
  },
  gateways: {
    title: 'Connected providers',
    empty: 'Connect the payment provider you already use. Your restaurants will be onboarded under it.',
    connect_another: 'Connect a provider',
    support_hint:
      'Once a restaurant is taking live payments, changing its provider is planned with us — write to {{email}}.',
    kinds: {
      stripe_connect: 'Stripe Connect',
      spreedly: 'Payment gateway',
      platform_adyen: 'FoodAmigos payments',
    },
    statuses: {
      connected: 'Connected',
      disconnected: 'Disconnected',
      needs_attention: 'Needs attention',
    },
    issue: {
      connect_not_enabled: {
        title: 'Stripe Connect is not enabled on the account behind {{name}}',
        description:
          'Stripe cannot create accounts for your restaurants until Connect is enabled. Enable it in your Stripe Dashboard, then check again here.',
      },
      account_restricted: {
        title: 'Stripe is holding back new accounts under {{name}}',
        description:
          'Stripe has temporarily restricted your account from creating connected accounts. Log in to your Stripe Dashboard, confirm that you intended to create these accounts, then check again here.',
      },
      loss_liability_unaccepted: {
        title: 'Stripe needs {{name}} to accept loss liability first',
        description:
          'Your Stripe platform has not yet accepted responsibility for negative balances on your restaurants’ accounts, so Stripe will not create them. In your Stripe Dashboard, open Settings → Connect → Platform profile and complete the loss liability acknowledgement, then check again here. You can ignore Stripe’s note about Accounts v2 below.',
      },
      account_creation_refused: {
        title: 'Stripe refused to create a restaurant account under {{name}}',
        description: 'Review the message below in your Stripe Dashboard, then check again here.',
      },
      key_invalid: {
        title: 'Stripe no longer accepts the key you connected for {{name}}',
        description:
          'The key was rolled, revoked or has expired. Create a new key in your Stripe Dashboard, then replace it here.',
      },
      key_access_limited: {
        title: 'Stripe has limited the key you connected for {{name}}',
        description:
          'A limited key cannot create transfers or payouts for your restaurants. In your Stripe Dashboard, open Developers → API keys and restore the key’s full access, then check again here — or roll the key and replace it here.',
      },
      provider_said: 'What Stripe said: “{{message}}”',
      hint: 'Until this is resolved, restaurants linked to this provider cannot start taking online payments.',
      open_stripe: 'Open Stripe Dashboard',
      check_again: 'Check again',
      replace_key: 'Replace key',
      cleared: 'Stripe accepted your restaurants again',
      still_blocked: 'Stripe still refuses — see what it said above',
    },
    refused: {
      provider_said: 'What {{name}} said: “{{message}}”',
      reference: 'Reference:',
      contact_support: 'Email support',
      support_subject: 'Payment provider connect refused: {{name}} (ref {{reference}})',
      account_in_test_mode: {
        action: 'Switch Test Mode off in Authorize.Net, under {{path}}, then try again.',
        open: 'Open Authorize.Net',
      },
      processor_not_configured: {
        action:
          'Call your merchant service provider, the company that opened your Authorize.Net account, and ask them to finish setting it up with its payment processor. New keys will not change this. Then try again.',
        action_card_connect:
          'Ask CardConnect support to enable card-not-present (e-commerce) authorisations on this merchant ID. New API credentials will not change this. Then try again.',
      },
    },
    table: {
      primary_tag: 'Default',
      live_count_one: '{{count}} taking payments',
      live_count_other: '{{count}} taking payments',
      attention_count_one: '{{count}} needs attention',
      attention_count_other: '{{count}} need attention',
      columns: {
        connection: 'Provider',
        status: 'Status',
        restaurants: 'Restaurants',
        methods: 'Local methods',
      },
      methods_count: '{{enabled}} of {{capable}} on',
    },
    picker: {
      recommended: 'Recommended',
      connect: 'Connect',
      stripe_connect: {
        title: 'Stripe Connect',
        description:
          'Use your own Stripe account. Each restaurant completes Stripe’s own onboarding; payouts and fees stay with Stripe.',
        already_connected: 'Already connected. Use Edit credentials in the list to replace its key.',
        already_connected_short: 'Already connected.',
      },
      stripe_direct: {
        title: 'Stripe (direct)',
        description: 'Charge every restaurant through one Stripe account of yours.',
        unavailable: 'Not available in your region yet.',
      },
      paypal: {
        direct: 'Offer PayPal from your own PayPal business account.',
        braintree: 'Take cards, PayPal and Venmo through your Braintree account.',
      },
      other: {
        title: 'Other providers',
        search: 'Search providers',
        loading: 'Loading providers…',
        no_match: 'No provider called “{{query}}”.',
        none_available: 'No other providers are available in your region yet.',
        load_error: 'The list of providers could not be loaded.',
        retry: 'Try again',
        pinned_match: 'That provider is pinned above.',
      },
      not_listed: {
        action: 'My provider isn’t listed',
        subject: 'Payment provider request: {{query}}',
      },
      use_foodamigos: {
        no_account: 'I don’t have an account with any of these',
        switch: 'Switch to FoodAmigos payments',
      },
    },
    orchestrated: {
      title: 'Connect {{name}}',
      replace_title: 'Replace the credentials for {{name}}',
      intro: 'Enter the credentials from your {{name}} account. We check them with {{name}} before anything is saved.',
      auth_mode: 'How you sign in to this provider',
      checking: 'Checking with {{name}}…',
      paypal_direct: {
        intro:
          'Paste the Client ID and Secret of the PayPal REST app your restaurants should take PayPal through. We check them with PayPal before anything is saved.',
        client_id: 'Client ID',
        secret: 'Secret',
        hint: 'PayPal Developer Dashboard → Apps & Credentials, on the Live tab. Use a REST app of your business account.',
        hint_sandbox:
          'PayPal Developer Dashboard → Apps & Credentials, on the Sandbox tab: this environment only takes sandbox apps.',
      },
      braintree: {
        intro:
          'Enter the API keys of your Braintree account. PayPal and Venmo work once they are linked in your Braintree Control Panel — you switch them on per connection afterwards.',
      },
      authorize_net: {
        intro:
          'Enter the API Login ID and Transaction Key of your Authorize.Net account. We check them with a small test authorisation: your Authorize.Net transaction log shows it as a declined $1.00, and no money moves. The account must be in Live Mode, because in Test Mode Authorize.Net refuses every real payment.',
        hint: 'You find both in the Authorize.Net Merchant Interface, under {{path}}.',
      },
      card_connect: {
        intro:
          'Enter the merchant ID, username and password of your live CardConnect (CardPointe) account. We check them with a 1.00 authorisation that we void at once: your CardPointe transaction log shows it as a declined or voided 1.00, and no money moves.',
      },
      stripe_direct: {
        intro:
          'Paste the secret key or a restricted key of the Stripe account every restaurant should charge through. We set up the webhook Stripe needs for you.',
        secret_key: 'Stripe secret or restricted key',
        hint: 'Stripe → Developers → API keys. Begins with sk_ (secret) or rk_ (restricted). A restricted key needs Write access to Charges and Refunds, Customers, PaymentIntents, PaymentMethods, SetupIntents, Tokens and Webhook Endpoints. No publishable key is needed.',
        prefix_error: 'Stripe secret keys begin with sk_, restricted keys with rk_.',
      },
      connected: {
        title: '{{name}} is connected.',
        description:
          'We’ll use these credentials for all your restaurants. Each restaurant still adds its business details.',
      },
      errors: {
        generic: 'We could not connect {{name}}',
      },
    },
    stripe: {
      title: 'Connect Stripe Connect',
      rotate_title: 'Replace your Stripe Connect key',
      rotate_intro:
        'Paste a new secret key and publishable key from the same Stripe account. Your restaurants stay connected.',
      intro:
        'Paste the secret key and the publishable key of your Stripe account. Both are under Developers → API keys.',
      verify_and_connect: 'Verify and connect',
      checking: 'Checking with Stripe…',
      secret_key: {
        label: 'Secret key',
        hint: 'Stripe → Developers → API keys. Begins with sk_.',
        prefix_error: 'Secret keys begin with sk_. This looks like a different kind of key.',
      },
      restricted_key: {
        label: 'Restricted API key',
        hint: 'Stripe → Developers → API keys → Create restricted key. Begins with rk_. Set it up as described above.',
        prefix_error: 'Restricted keys begin with rk_. This looks like a different kind of key.',
      },
      use_restricted_key: 'Use a restricted key instead',
      use_secret_key: 'Use the secret key instead',
      publishable_key: {
        label: 'Publishable key',
        hint: 'On the same Stripe page. Begins with pk_. Your storefronts need it to accept cards.',
        prefix_error: 'Publishable keys begin with pk_.',
        mode_mismatch: 'One key is live and the other is a test key. Both must come from the same Stripe mode.',
      },
      name: {
        label: 'Name this connection',
        hint: 'Optional. Shown wherever this provider is listed.',
        placeholder: 'Stripe Connect',
      },
      permissions: {
        title: 'How to create this key',
        start_blank:
          'When Stripe asks how you will use the key, choose “{{option}}”. It starts with every permission set to {{none}}.',
        intro:
          'Then set the switch on each of these section rows. It sets every permission in that section, so there is no need to go through them one by one.',
        rows_checked: 'We check: {{rows}}',
        secret_key_hint: 'Leave every other section at {{none}}. A secret key needs none of this.',
        open_stripe: 'Open your Stripe API keys',
      },
      login_setting: {
        title: 'Turn off Stripe’s login for your restaurants',
        description:
          'In your Stripe Dashboard, open Settings → Connect → Payouts → External accounts and switch off “{{setting}}”. While it is on, Stripe makes each restaurant create a Stripe login with a phone number during onboarding and asks for an SMS code on every later visit, so whoever does not have that phone cannot continue.',
        bank_details: 'Nothing is lost: restaurants enter their bank details in our own form.',
        open_stripe: 'Open this Stripe setting',
      },
      errors: {
        generic: 'Stripe could not be reached',
      },
    },
    default_choice: {
      label: 'Use as default for restaurants',
      hint: 'New restaurants, and restaurants that have no provider yet, are linked to it automatically. Leave it unticked to pick a provider for each restaurant yourself.',
    },
    manage: {
      payment_methods: 'Payment methods',
      edit_credentials: 'Edit credentials',
      edit_credentials_unavailable: 'Edit credentials (this provider is not offered right now)',
      set_primary: 'Set as default for new restaurants',
      clear_primary: 'Stop using as default for new restaurants',
      rename: {
        action: 'Rename connection',
        title: 'Rename this connection',
        confirm: 'Save',
      },
      disconnect: {
        action: 'Disconnect',
        title: 'Disconnect {{name}}?',
        confirm: 'Disconnect',
        warning_one: '{{count}} restaurant is linked to it and will go back to Not set up.',
        warning_other: '{{count}} restaurants are linked to it and will go back to Not set up.',
        live: {
          title_one: 'Online payments pause for {{count}} live restaurant',
          title_other: 'Online payments pause for {{count}} live restaurants',
          description:
            'As soon as you disconnect, their customers can no longer pay online. Each one goes back to Not set up until you link it to another provider.',
          refunds: 'Orders already paid through this provider can no longer be refunded from FoodAmigos.',
          pause_one: 'Pause online payments for this restaurant',
          pause_other: 'Pause online payments for these {{count}} restaurants',
          confirm: 'Disconnect and pause payments',
        },
      },
      remove: {
        action: 'Remove from list',
        title: 'Remove {{name}}?',
        description: 'It is already disconnected and no restaurant uses it. It will no longer show in this list.',
        confirm: 'Remove',
      },
    },
    payment_methods: {
      title: 'Payment methods for {{name}}',
      description:
        'Choose the local methods your restaurants offer through this provider. Each one shows only where the restaurant’s currency matches.',
      save: 'Save',
      publishable_key: {
        label: 'Stripe publishable key',
        hint: 'Stripe needs it to show the payment form. Find it in your Stripe Dashboard under Developers → API keys.',
        invalid: 'A publishable key here starts with {{prefix}}.',
        clear_to_save: 'It belongs to Stripe’s other mode. Clear it to save with every local method off.',
      },
      on_other_connection: 'already switched on for {{connection}}',
      other_currency: 'not offered in {{currency}}',
      delivery_only: '{{name}} (delivery orders only — it needs the customer’s address)',
      invite_only:
        '{{name}} (Stripe offers it by invitation only — request access in your Stripe dashboard first, or Stripe refuses every payment)',
      also_enable: {
        stripe:
          'Turn the same methods on in your Stripe Dashboard under Settings → Payment methods, or Stripe refuses them.',
        provider: 'Your contract with this provider has to include the methods you turn on here.',
        braintree:
          'Link PayPal and Venmo in your Braintree Control Panel under Settings → Processing first, or Braintree refuses them. Venmo is for US restaurants charging in dollars.',
        paypal_direct: 'PayPal payments are paid into this PayPal business account. For Venmo, connect Braintree.',
      },
    },
    actions: {
      cancel: 'Cancel',
    },
    toasts: {
      load_error: 'Failed to load your connected providers',
      credentials_replaced: 'Credentials replaced',
      save_error: 'Failed to save',
      connected: 'Stripe Connect is connected',
      renamed: 'Connection renamed',
      primary_set: 'Default provider updated',
      primary_cleared: 'Default provider removed. New restaurants are not linked to a provider until you pick one.',
      disconnected: 'Provider disconnected',
      removed: 'Provider removed',
      payment_methods_saved: 'Payment methods saved',
    },
  },
  restaurants: {
    title: 'Restaurants',
    description: 'Where each restaurant stands on taking online payments.',
    search: 'Search restaurants',
    filters: {
      all: 'All ({{count}})',
      needs_attention: 'Needs attention ({{count}})',
    },
    empty: 'No restaurants yet.',
    empty_attention: 'Nothing needs your attention.',
    columns: {
      restaurant: 'Restaurant',
      provider: 'Provider',
      status: 'Status',
      action: 'Action',
    },
    no_provider: '—',
    foodamigos_payments: 'FoodAmigos payments',
    groups: {
      not_set_up: 'Not set up',
      business_details_missing: 'Business details missing',
      hosted_not_finished: '{{provider}} form not finished',
      hosted_not_finished_generic: 'Provider form not finished',
      waiting_for_provider: 'Waiting for provider',
      action_needed: 'Action needed',
      payments_live: 'Payments live',
      online_payments_off: 'Online payments off',
      waiting_for_partner: 'Waiting for you',
    },
    link_expired: 'Link expired',
    actions: {
      set_up: 'Set up',
      change: 'Change',
      unlink: 'Unlink',
      remind: 'Remind owner',
      reminded: 'Reminded · again {{time}}',
      start_over: 'Start Stripe setup over',
    },
    assign: {
      title: 'Which provider should {{name}} use?',
      label: 'Provider',
      confirm: 'Save',
      cancel: 'Cancel',
      primary: 'Default',
      none: 'Connect a provider first.',
    },
    unlink: {
      title: 'Unlink {{name}} from {{provider}}?',
      title_generic: 'Unlink {{name}} from its provider?',
      description:
        'The restaurant goes back to Not set up, so you can pick the right provider for it. The provider stays connected for your other restaurants.',
      confirm: 'Unlink',
    },
    pause: {
      title: 'Online payments pause for {{name}}',
      description:
        'Its customers can no longer pay online until the restaurant is set up and verified with a provider again.',
      refunds: 'Orders already paid through the current provider can no longer be refunded from FoodAmigos.',
      confirm: 'Pause online payments for this restaurant',
    },
    start_over: {
      title: 'Start the Stripe setup of {{name}} over?',
      points: {
        not_used: 'Its current Stripe account ({{account}}) will no longer be used for this restaurant.',
        kept: 'The account is not deleted: it stays in your Stripe, where you can reject it.',
        fresh: 'The next “Continue” starts again from Stripe’s first page, and a different email can be used.',
        paused: 'If the restaurant takes online payments, they pause until the new setup is finished.',
      },
      confirm: 'Start over',
      blocked: {
        received_payments:
          'This restaurant’s Stripe account has received payments, so its setup cannot be started over. Contact support to change it.',
        partner_action_needed: 'Fix the issue shown on your Stripe Connect first.',
      },
    },
    toasts: {
      load_error: 'Failed to load your restaurants',
      assigned: 'Provider saved',
      assign_error: 'Failed to save the provider',
      unlinked: 'Restaurant unlinked',
      unlink_error: 'Failed to unlink the restaurant',
      reminded: 'Reminder sent to the owner',
      remind_error: 'Failed to send the reminder',
      cooling_down: 'The owner was already reminded today',
      started_over: 'Stripe setup started over',
      start_over_error: 'Failed to start the Stripe setup over',
    },
  },
  footer: {
    text: 'Your FoodAmigos service fee is billed to you whichever provider a restaurant uses.',
    link: 'See Billing',
  },
};
